import { mutate } from "swr";
import { SWR_KEYS } from "./api";

/**
 * SWR キャッシュの localStorage 永続化
 *
 * /services のカタログ系データ（サービス一覧・タグ一覧）だけを localStorage に保存し、
 * リロード直後・再訪問時に前回データで瞬時に描画 → バックグラウンド更新（useCatalogCache）
 * で最新化する（stale-while-revalidate）。他の SWR キーは毎回取得のまま。
 *
 * 注意: SWR の custom cache provider は使わない。グローバル `mutate`（"swr" から import）は
 * デフォルトキャッシュに固定束縛されており、provider を挿すと useServices 等の楽観的更新が
 * 分離先のキャッシュに届かなくなるため、デフォルトキャッシュへの注入・読み出しで実現する。
 */

const STORAGE_KEY = "ai-deck-swr-cache";
/** 永続化フォーマットの版数。データ形状を変えたらインクリメントして旧キャッシュを無効化する */
const STORAGE_VERSION = 1;

/** 永続化を許可する SWR キー。キー文字列の正本は SWR_KEYS（カタログ系のみ、favoritesOnly は対象外） */
export const PERSISTED_SWR_KEYS: ReadonlySet<string> = new Set([
  SWR_KEYS.SERVICES_V1("en"),
  SWR_KEYS.SERVICES_V1("ja"),
  SWR_KEYS.TAGS,
]);

interface PersistedCache {
  v: number;
  entries: Array<[string, unknown]>;
}

/**
 * ユーザー私的メモ（Layer 3 の userPersonalNotes）は localStorage に残さない。
 * 復元直後は null になるが、認証確定後の鮮度更新で即座に埋まる
 */
function stripPrivateFields(data: unknown): unknown {
  if (!data || typeof data !== "object") return data;
  const record = data as Record<string, unknown>;
  if (!Array.isArray(record.services)) return data;
  return {
    ...record,
    services: record.services.map((service: unknown) =>
      service && typeof service === "object"
        ? { ...(service as Record<string, unknown>), userPersonalNotes: null }
        : service,
    ),
  };
}

/** localStorage から前回データを読み出す。allowlist 外・版数不一致・破損データは捨てる */
export function readPersistedEntries(): Array<[string, unknown]> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Partial<PersistedCache>;
    if (parsed.v !== STORAGE_VERSION || !Array.isArray(parsed.entries)) return [];
    return parsed.entries.filter(
      ([key, data]) => typeof key === "string" && PERSISTED_SWR_KEYS.has(key) && data !== undefined,
    );
  } catch {
    // 壊れたキャッシュは無視して空扱い（次回の書き出しで上書きされる）
    return [];
  }
}

/**
 * アプリ起動時（React レンダリング前）に 1 回呼び、SWR デフォルトキャッシュへ
 * 前回セッションのカタログデータを注入する。キャッシュ未保存（未ログイン・初訪問）なら何もしない
 */
export function restoreSwrCache(): void {
  for (const [key, data] of readPersistedEntries()) {
    mutate(key, data, { revalidate: false });
  }
}

// 直前に書き出した各キーの data 参照。無変化時の再直列化（数百 KB の JSON.stringify）を避ける
let lastPersistedStates: Map<string, unknown> | null = null;

/** persistSwrCache が読むキャッシュの最小インターフェース（SWR の Cache 互換） */
interface ReadableSwrCache {
  get(key: string): { data?: unknown } | undefined;
}

/**
 * SWR キャッシュから allowlist 分だけを localStorage へ書き出す。
 * pagehide / visibilitychange(hidden) のたびに呼ばれるため、データ参照が
 * 前回書き出しから変わっていなければ何もしない（dirty チェック）
 */
export function persistSwrCache(cache: ReadableSwrCache): void {
  const states = new Map<string, unknown>();
  for (const key of PERSISTED_SWR_KEYS) {
    const data = cache.get(key)?.data;
    if (data !== undefined) states.set(key, data);
  }

  if (
    lastPersistedStates !== null &&
    states.size === lastPersistedStates.size &&
    [...states].every(([key, data]) => lastPersistedStates?.get(key) === data)
  ) {
    return;
  }

  try {
    const payload: PersistedCache = {
      v: STORAGE_VERSION,
      entries: [...states].map(([key, data]) => [key, stripPrivateFields(data)]),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    lastPersistedStates = states;
  } catch {
    // 容量超過・プライベートモード等では永続化を諦める（アプリ動作には影響しない）
  }
}

/**
 * ログイン・ログアウト時に呼ぶ。アカウント切り替えで前ユーザーのデータ
 * （お気に入り状態等を含むサービス一覧）が次ユーザーに見えるのを防ぐ
 */
export function clearSwrCache(): void {
  for (const key of PERSISTED_SWR_KEYS) {
    mutate(key, undefined, { revalidate: false });
  }
  lastPersistedStates = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage が使えない環境では何もしない
  }
}
